import { LoginDesktopComponent } from '../../components/Login/login-desktop';
import { LayoutWrapper } from '../../layouts';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { getServerTranslations } from '../../lib/i18n/getServerTranslations';
import { arrivalDestination, consumeLoginTokenFromUrl } from '@restworld/feature-authentication';
import { ogLoginImageUrl } from '@restworld/utils-common';

const LoginDesktopPage = () => {
  const { t } = useTranslation('auth-flow');
  const router = useRouter();
  const redirectToPath = (path: string, query?: { [key: string]: string | boolean }) => {
    router.push({
      pathname: path,
      query: query,
    });
  };
  // The token was taken out of the address bar while the bundle was evaluated, so it is
  // read back from there. `router.query` stays as a fallback: it still holds the token
  // when Next parsed the URL before the cleanup, and it costs nothing to keep.
  const token = consumeLoginTokenFromUrl() || router?.query?.token?.toString() || '';
  const email = router?.query?.email?.toString() || undefined;
  // `arrivalDestination()` first: this prop becomes `activeUrl` on the e-mail form, which
  // sends it to `passwordlessLogin` so the link we mint lands where the visitor was going. On a
  // cold load `router.query` is still empty, and a visitor who typed fast enough would have got
  // a link back to nowhere. The remembered value was read before any of that.
  let redirectUrl = arrivalDestination() ?? router?.query?.redirect_url?.toString();

  if (router.query?.redirectTo) {
    const redirectToLogin =
      router.query?.redirectTo &&
      (Array.isArray(router.query.redirectTo)
        ? decodeURIComponent(router.query.redirectTo[0])
        : decodeURIComponent(router.query.redirectTo));
    const redirectParamsLogin =
      router.query?.redirectParams &&
      (Array.isArray(router.query.redirectParams)
        ? JSON.parse(decodeURIComponent(router.query.redirectParams[0]))
        : JSON.parse(decodeURIComponent(router.query.redirectParams)));
    if (!!redirectToLogin && !!redirectParamsLogin) {
      redirectUrl = `${redirectToLogin}?${new URLSearchParams(redirectParamsLogin)}`;
    } else if (redirectToLogin) {
      redirectUrl = redirectToLogin;
    }
  }

  // The card an access link unfurls into, which is a narrower set of places than
  // "everywhere we send one". A magic link inside an email is an HTML button
  // (`user_magic_link.html.eex`) and a mail client never unfurls it, and the
  // phone login sends an OTP rather than a URL. What does unfurl: the links sent
  // as bare URLs, such as the employer SMS built by
  // `daily_recommended_candidates_sms_send_job` (shortened, and the shortener
  // answers a 308, so a crawler follows it here), and any link somebody pastes
  // into Slack or WhatsApp. That last case is the one that started this.
  //
  // Only `images` is set, so the merge in `wrapper-layout` keeps the rest of the
  // default Open Graph block, and `og:title` and `og:description` keep coming
  // from the page title and description above.
  //
  // `twitter:image` is written by hand for the same reason `showcase-v2` writes
  // it: next-seo 5.15 builds `twitter:card` and nothing else, so without this tag
  // the page keeps promising `summary_large_image` and hands X no image of its
  // own. The dimensions are the file's real ones.
  const ogImageUrl = ogLoginImageUrl();
  const seoProps = {
    openGraph: {
      images: [{ url: ogImageUrl, alt: t('login_page_title'), width: 1200, height: 630 }],
    },
    additionalMetaTags: [
      { name: 'twitter:image', content: ogImageUrl },
      { name: 'twitter:image:alt', content: t('login_page_title') },
    ],
  };

  return (
    <LayoutWrapper
      title={t('login_page_title')}
      showSearchBarInNavbar={false}
      noHorizontalPadding
      noPaddingBottom
      noPaddingTop
      metaDescription={t('login_page_meta_description')}
      seoProps={seoProps}
    >
      <h1 className="sr-only">{t('login_page_heading')}</h1>
      <LoginDesktopComponent {...{ redirectUrl, redirectToPath, token, email }} />
    </LayoutWrapper>
  );
};

export const getStaticProps = async ({ locale }) => {
  const transConfig = await getServerTranslations(locale, ['common', 'auth-flow', 'onboarding-page']);
  return {
    props: {
      ...transConfig,
    },
  };
};

export default LoginDesktopPage;
